import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel,
} from '../../components/ui/dropdown-menu';
import { toast } from 'sonner';
import {
  Plus, Search, Edit, Trash2, Loader2, MoreHorizontal,
  Printer, Mail, Eye, Send, FileText, Palette, Copy, FileCheck, RefreshCw, ArrowRight
} from 'lucide-react';
import api from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import LineItemsForm from '../../components/LineItemsForm';

import { generateDocumentHTML, resolveDocumentTemplate } from '../../lib/documentTemplates';
import AttachmentsSection from '../../components/AttachmentsSection';

const STATUS_COLORS = {
  draft: 'bg-slate-100 text-slate-700',
  sent: 'bg-blue-100 text-blue-700',
  accepted: 'bg-green-100 text-green-700',
  rejected: 'bg-red-100 text-red-700',
  expired: 'bg-amber-100 text-amber-700',
  revised: 'bg-purple-100 text-purple-700',
};

const Quotations = () => {
  const { currentCompany } = useAuth();
  const [quotations, setQuotations] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  

  // Dialog states
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [isEmailOpen, setIsEmailOpen] = useState(false);
  const [isConvertOpen, setIsConvertOpen] = useState(false);
  const [editingQuotation, setEditingQuotation] = useState(null);
  const [selectedQuotation, setSelectedQuotation] = useState(null);
  const [previewHtml, setPreviewHtml] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [emailConfigured, setEmailConfigured] = useState(false);
  const [convertType, setConvertType] = useState('');

  // Form state
  const [formData, setFormData] = useState({
    client_id: '',
    items: [{ id: '1', product_id: '', description: '', quantity: 1, unit_price: 0, tax_rate: '0', tax_amount: 0, subtotal: 0, line_total: 0 }],
    discount: 0,
    discount_type: 'fixed',
    valid_until: '',
    payment_terms: 'Net 30',
    notes: '',
    status: 'draft',
  });

  const [emailData, setEmailData] = useState({
    recipient_email: '',
    recipient_name: '',
    subject: '',
    message: ''
  });

  const fetchData = async () => {
    setLoading(true);
    try {
      const [quotationsRes, clientsRes, emailStatusRes] = await Promise.all([
        api.get('/quotations'),
        api.get('/clients'),
        api.get('/email/status')
      ]);
      setQuotations(quotationsRes.data);
      setClients(clientsRes.data);
      setEmailConfigured(emailStatusRes.data.configured);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const getClient = (clientId) => clients.find(c => c.id === clientId);

  // Calculate totals from items
  const calculateTotals = (items, discount, discountType) => {
    const subtotal = items.reduce((sum, item) => sum + (item.subtotal || 0), 0);
    const tax = items.reduce((sum, item) => sum + (item.tax_amount || 0), 0);
    const discountAmount = discountType === 'percent' 
      ? (subtotal * (parseFloat(discount) || 0) / 100)
      : (parseFloat(discount) || 0);
    const total = subtotal + tax - discountAmount;
    return { subtotal, tax, discount: discountAmount, total };
  };

  const openForm = (quotation = null) => {
    if (quotation) {
      setEditingQuotation(quotation);
      setFormData({
        client_id: quotation.client_id || '',
        items: quotation.items?.length > 0 ? quotation.items : [{ id: '1', product_id: '', description: '', quantity: 1, unit_price: 0, tax_rate: '0', tax_amount: 0, subtotal: 0, line_total: 0 }],
        discount: quotation.discount || 0,
        discount_type: quotation.discount_type || 'fixed',
        valid_until: quotation.valid_until || '',
        payment_terms: quotation.payment_terms || 'Net 30',
        notes: quotation.notes || '',
        status: quotation.status || 'draft',
      });
    } else {
      setEditingQuotation(null);
      setFormData({
        client_id: '',
        items: [{ id: '1', product_id: '', description: '', quantity: 1, unit_price: 0, tax_rate: '0', tax_amount: 0, subtotal: 0, line_total: 0 }],
        discount: 0,
        discount_type: 'fixed',
        valid_until: '',
        payment_terms: 'Net 30',
        notes: '',
        status: 'draft',
      });
    }
    setIsFormOpen(true);
  };

  const handleSubmitForm = async (e) => {
    e.preventDefault();
    if (!formData.client_id) {
      toast.error('Please select a client');
      return;
    }
    if (formData.items.length === 0 || !formData.items[0].product_id) {
      toast.error('Please select a product for each line item');
      return;
    }

    setSubmitting(true);
    try {
      const totals = calculateTotals(formData.items, formData.discount, formData.discount_type);
      const payload = {
        ...formData,
        subtotal: totals.subtotal,
        tax: totals.tax,
        discount_amount: totals.discount,
        total: totals.total,
      };

      if (editingQuotation) {
        await api.put(`/quotations/${editingQuotation.id}`, payload);
        toast.success('Quotation updated');
      } else {
        await api.post('/quotations', payload);
        toast.success('Quotation created');
      }
      setIsFormOpen(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to save quotation');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this quotation?')) {
      try {
        await api.delete(`/quotations/${id}`);
        toast.success('Quotation deleted');
        fetchData();
      } catch (error) {
        toast.error('Failed to delete');
      }
    }
  };

  // Preview with template
  const openPreview = (quotation) => {
    setSelectedQuotation(quotation);
    const client = getClient(quotation.client_id);
    const docWithClient = {
      ...quotation,
      client_name: client?.name || 'Client',
      client_email: client?.email || '',
      client_phone: client?.phone || '',
      client_address: client?.address || '',
    };
    const html = generateDocumentHTML(resolveDocumentTemplate(docWithClient, currentCompany, 'quotation'), 'quotation', docWithClient, currentCompany);
    setPreviewHtml(html);
    setIsPreviewOpen(true);
  };

  
    

  const handlePrint = (quotation) => {
    const client = getClient(quotation.client_id);
    const docWithClient = {
      ...quotation,
      client_name: client?.name || 'Client',
      client_email: client?.email || '',
      client_phone: client?.phone || '',
      client_address: client?.address || '',
    };
    const html = generateDocumentHTML(resolveDocumentTemplate(docWithClient, currentCompany, 'quotation'), 'quotation', docWithClient, currentCompany);
    const printWindow = window.open('', '_blank');
    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => printWindow.print(), 250);
  };

  // Email handlers
  const openEmailDialog = (quotation) => {
    const client = getClient(quotation.client_id);
    setSelectedQuotation(quotation);
    setEmailData({
      recipient_email: client?.email || '',
      recipient_name: client?.name || '',
      subject: `Quotation #${quotation.id.slice(0, 8).toUpperCase()} from ${currentCompany?.name || 'Elite Group'}`,
      message: 'Please find attached your quotation. Let us know if you have any questions.'
    });
    setIsEmailOpen(true);
  };

  const handleSendEmail = async (e) => {
    e.preventDefault();
    if (!emailConfigured) {
      toast.error('Email service not configured');
      return;
    }
    setSubmitting(true);
    try {
      await api.post(`/quotations/${selectedQuotation.id}/send-email`, emailData);
      toast.success(`Quotation sent to ${emailData.recipient_email}`);
      setIsEmailOpen(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to send email');
    } finally {
      setSubmitting(false);
    }
  };

  // Document conversion handlers
  const openConvertDialog = (quotation, type) => {
    setSelectedQuotation(quotation);
    setConvertType(type);
    setIsConvertOpen(true);
  };

  const handleConvert = async () => {
    setSubmitting(true);
    try {
      const client = getClient(selectedQuotation.client_id);
      const baseData = {
        client_id: selectedQuotation.client_id,
        items: selectedQuotation.items,
        subtotal: selectedQuotation.subtotal,
        tax: selectedQuotation.tax,
        discount: selectedQuotation.discount,
        discount_type: selectedQuotation.discount_type,
        discount_amount: selectedQuotation.discount_amount,
        total: selectedQuotation.total,
        notes: selectedQuotation.notes,
        payment_terms: selectedQuotation.payment_terms,
        source_quotation_id: selectedQuotation.id,
      };

      let endpoint = '';
      let successMsg = '';

      switch (convertType) {
        case 'proforma':
          endpoint = '/invoices';
          baseData.status = 'proforma';
          baseData.invoice_type = 'proforma';
          successMsg = 'Proforma Invoice created';
          break;
        case 'invoice':
          endpoint = '/invoices';
          baseData.status = 'draft';
          baseData.invoice_type = 'standard';
          baseData.due_date = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
          successMsg = 'Invoice created';
          break;
        case 'sales_order':
          endpoint = '/sales-orders';
          baseData.status = 'pending';
          successMsg = 'Sales Order created';
          break;
        case 'revised':
          endpoint = '/quotations';
          baseData.status = 'draft';
          baseData.parent_quotation_id = selectedQuotation.id;
          baseData.revision_number = (selectedQuotation.revision_number || 0) + 1;
          successMsg = 'Revised Quotation created';
          // Mark original as revised
          await api.put(`/quotations/${selectedQuotation.id}`, { ...selectedQuotation, status: 'revised' });
          break;
        default:
          throw new Error('Unknown conversion type');
      }

      await api.post(endpoint, baseData);
      toast.success(successMsg);
      setIsConvertOpen(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to convert quotation');
      console.error(error);
    } finally {
      setSubmitting(false);
    }
  };

  const filteredQuotations = quotations.filter(q => {
    const client = getClient(q.client_id);
    return (
      q.id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      client?.name?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin text-teal-600" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900">Quotations</h1>
          <p className="text-slate-500 mt-1">Create and manage sales quotations with line items</p>
        </div>
        <Button onClick={() => openForm()} className="bg-teal-700 hover:bg-teal-800">
          <Plus className="h-4 w-4 mr-2" />
          Create Quotation
        </Button>
      </div>

      {/* Search */}
      <Card>
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              placeholder="Search quotations..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
        </CardContent>
      </Card>

      {/* Quotations Table */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead>Quotation #</TableHead>
                  <TableHead>Client</TableHead>
                  <TableHead>Items</TableHead>
                  <TableHead className="text-right">Total</TableHead>
                  <TableHead>Valid Until</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredQuotations.length > 0 ? (
                  filteredQuotations.map((quotation) => {
                    const client = getClient(quotation.client_id);
                    return (
                      <TableRow key={quotation.id} className="hover:bg-slate-50">
                        <TableCell className="font-medium">
                          #{quotation.id.slice(0, 8).toUpperCase()}
                          {quotation.revision_number > 0 && (
                            <Badge variant="outline" className="ml-2 text-xs">
                              Rev {quotation.revision_number}
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell>{client?.name || '-'}</TableCell>
                        <TableCell>
                          <Badge variant="outline">{quotation.items?.length || 0} items</Badge>
                        </TableCell>
                        <TableCell className="text-right font-medium">
                          ${(quotation.total || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                        </TableCell>
                        <TableCell>
                          {quotation.valid_until ? new Date(quotation.valid_until).toLocaleDateString() : '-'}
                        </TableCell>
                        <TableCell>
                          <Badge className={STATUS_COLORS[quotation.status] || STATUS_COLORS.draft}>
                            {quotation.status?.charAt(0).toUpperCase() + quotation.status?.slice(1)}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm">
                                <MoreHorizontal className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-48">
                              <DropdownMenuLabel>Document Actions</DropdownMenuLabel>
                              <DropdownMenuItem onClick={() => openPreview(quotation)}>
                                <Eye className="h-4 w-4 mr-2" />Preview
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handlePrint(quotation)}>
                                <Printer className="h-4 w-4 mr-2" />Print
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => openEmailDialog(quotation)} disabled={!emailConfigured}>
                                <Mail className="h-4 w-4 mr-2" />Send Email
                              </DropdownMenuItem>
                              
                              <DropdownMenuSeparator />
                              <DropdownMenuLabel>Convert To</DropdownMenuLabel>
                              <DropdownMenuItem onClick={() => openConvertDialog(quotation, 'proforma')}>
                                <FileCheck className="h-4 w-4 mr-2" />Proforma Invoice
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => openConvertDialog(quotation, 'invoice')}>
                                <FileText className="h-4 w-4 mr-2" />Invoice
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => openConvertDialog(quotation, 'sales_order')}>
                                <ArrowRight className="h-4 w-4 mr-2" />Sales Order
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => openConvertDialog(quotation, 'revised')}>
                                <RefreshCw className="h-4 w-4 mr-2" />Revised Quotation
                              </DropdownMenuItem>
                              
                              <DropdownMenuSeparator />
                              <DropdownMenuItem onClick={() => openForm(quotation)}>
                                <Edit className="h-4 w-4 mr-2" />Edit
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleDelete(quotation.id)} className="text-red-600">
                                <Trash2 className="h-4 w-4 mr-2" />Delete
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    );
                  })
                ) : (
                  <TableRow>
                    <TableCell colSpan={7} className="h-32 text-center text-slate-500">
                      <FileText className="h-8 w-8 mx-auto mb-2 text-slate-300" />
                      No quotations found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Create/Edit Form Dialog */}
      <Dialog open={isFormOpen} onOpenChange={setIsFormOpen}>
        <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <FileText className="h-5 w-5 text-teal-600" />
              {editingQuotation ? 'Edit Quotation' : 'Create Quotation'}
            </DialogTitle>
            <DialogDescription>
              Add items, set pricing, and configure quotation details
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmitForm}>
            {/* Line Items Form */}
            <LineItemsForm
              items={formData.items}
              onItemsChange={(items) => setFormData({ ...formData, items })}
              clients={clients}
              onClientsChange={setClients}
              selectedClientId={formData.client_id}
              onClientChange={(id) => setFormData({ ...formData, client_id: id })}
              discount={formData.discount}
              onDiscountChange={(val) => setFormData({ ...formData, discount: val })}
              discountType={formData.discount_type}
              onDiscountTypeChange={(val) => setFormData({ ...formData, discount_type: val })}
              partyType="client"
              partyLabel="Client"
            />

            {/* Additional Fields */}
            <div className="grid grid-cols-2 gap-4 mt-6">
              <div className="grid gap-2">
                <Label>Valid Until</Label>
                <Input
                  type="date"
                  value={formData.valid_until}
                  onChange={(e) => setFormData({ ...formData, valid_until: e.target.value })}
                />
              </div>
              <div className="grid gap-2">
                <Label>Payment Terms</Label>
                <Select value={formData.payment_terms} onValueChange={(val) => setFormData({ ...formData, payment_terms: val })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Due on Receipt">Due on Receipt</SelectItem>
                    <SelectItem value="Net 15">Net 15</SelectItem>
                    <SelectItem value="Net 30">Net 30</SelectItem>
                    <SelectItem value="Net 45">Net 45</SelectItem>
                    <SelectItem value="Net 60">Net 60</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="grid gap-2 mt-4">
              <Label>Notes / Terms & Conditions</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Additional notes or terms..."
                rows={3}
              />
            </div>

            <DialogFooter className="mt-6">
              <Button type="button" variant="outline" onClick={() => setIsFormOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" className="bg-teal-700 hover:bg-teal-800" disabled={submitting}>
                {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                {editingQuotation ? 'Update Quotation' : 'Create Quotation'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={isPreviewOpen} onOpenChange={setIsPreviewOpen}>
        <DialogContent className="sm:max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <FileText className="h-5 w-5 text-teal-600" />
              Quotation Preview
            </DialogTitle>
            <DialogDescription>This document renders with the company's default template from Settings.</DialogDescription>
          </DialogHeader>
          
            
          <div className="border rounded-lg overflow-hidden mt-4" dangerouslySetInnerHTML={{ __html: previewHtml }} />
        <AttachmentsSection entityType="quotation" entityId={selectedQuotation?.id} />
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setIsPreviewOpen(false)}>Close</Button>
            <Button variant="outline" onClick={() => selectedQuotation && handlePrint(selectedQuotation)}>
              <Printer className="h-4 w-4 mr-2" />Print
            </Button>
            <Button className="bg-teal-700 hover:bg-teal-800" onClick={() => { setIsPreviewOpen(false); selectedQuotation && openEmailDialog(selectedQuotation); }} disabled={!emailConfigured}>
              <Mail className="h-4 w-4 mr-2" />Send Email
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Email Dialog */}
      <Dialog open={isEmailOpen} onOpenChange={setIsEmailOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Send className="h-5 w-5 text-teal-600" />
              Send Quotation via Email
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSendEmail}>
            <div className="grid gap-4 py-4">
              <div className="grid gap-2">
                <Label>Recipient Email *</Label>
                <Input type="email" value={emailData.recipient_email} onChange={(e) => setEmailData({...emailData, recipient_email: e.target.value})} required />
              </div>
              <div className="grid gap-2">
                <Label>Recipient Name *</Label>
                <Input value={emailData.recipient_name} onChange={(e) => setEmailData({...emailData, recipient_name: e.target.value})} required />
              </div>
              <div className="grid gap-2">
                <Label>Subject</Label>
                <Input value={emailData.subject} onChange={(e) => setEmailData({...emailData, subject: e.target.value})} />
              </div>
              <div className="grid gap-2">
                <Label>Message</Label>
                <Textarea value={emailData.message} onChange={(e) => setEmailData({...emailData, message: e.target.value})} rows={3} />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsEmailOpen(false)}>Cancel</Button>
              <Button type="submit" className="bg-teal-700 hover:bg-teal-800" disabled={submitting}>
                {submitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
                Send
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Convert Dialog */}
      <Dialog open={isConvertOpen} onOpenChange={setIsConvertOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">
              Convert Quotation
            </DialogTitle>
            <DialogDescription>
              {convertType === 'proforma' && 'Create a Proforma Invoice from this quotation'}
              {convertType === 'invoice' && 'Create a standard Invoice from this quotation'}
              {convertType === 'sales_order' && 'Create a Sales Order from this quotation'}
              {convertType === 'revised' && 'Create a revised version of this quotation'}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-slate-600">
              This will create a new{' '}
              <span className="font-medium">
                {convertType === 'proforma' && 'Proforma Invoice'}
                {convertType === 'invoice' && 'Invoice'}
                {convertType === 'sales_order' && 'Sales Order'}
                {convertType === 'revised' && 'Revised Quotation'}
              </span>{' '}
              with all items and details from Quotation #{selectedQuotation?.id.slice(0, 8).toUpperCase()}.
            </p>
            {convertType === 'revised' && (
              <p className="text-sm text-amber-600 mt-2">
                The original quotation will be marked as "Revised".
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsConvertOpen(false)}>Cancel</Button>
            <Button className="bg-teal-700 hover:bg-teal-800" onClick={handleConvert} disabled={submitting}>
              {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Create {convertType === 'proforma' ? 'Proforma' : convertType === 'sales_order' ? 'Sales Order' : convertType === 'revised' ? 'Revision' : 'Invoice'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Quotations;
